import { useEffect, useState, useCallback, useRef } from 'react';
import { isAppPWA, getDevicePlatform, isDownloadAvailable } from '../utils/helper-functions';
import posthog from 'posthog-js';
import {
  POSTHOG_CAPTURE_BANNER_IMPRESSION,
  POSTHOG_CAPTURE_BANNER_CLICK,
  POSTHOG_CAPTURE_BANNER_DISMISS,
} from '../posthog/constants';
import pikkoLogo from '../styles/img/logo_pikko_native_app.jpeg';

const DISMISS_KEY = 'pikko_banner_dismissed_at';
const DISMISS_COUNT_KEY = 'pikko_banner_dismiss_count';
const DISMISS_TTL = 24 * 60 * 60 * 1000; // 1 day

const SmartBanner = ({ delay = 0, screen = 'home' }) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [shimmer, setShimmer] = useState(false);
  const [pulse, setPulse] = useState(false);
  const visibleAtRef = useRef(null);

  const IOS_URL = process.env.REACT_APP_IOS_DOWNLOAD_URL;
  const ANDROID_URL = process.env.REACT_APP_ANDROID_DOWNLOAD_URL;

  const getPlatform = () => {
    const { isIOS, isAndroid } = getDevicePlatform();
    return isIOS ? 'ios' : isAndroid ? 'android' : 'desktop';
  };

  // Mount + slide-in with delay
  useEffect(() => {
    if (!isDownloadAvailable()) return;

    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_TTL) return;

    const mountTimer = setTimeout(() => {
      setMounted(true);
    }, delay);
    return () => clearTimeout(mountTimer);
  }, [delay]);

  // Slide-in after mount — double rAF ensures the browser paints the off-screen state first
  useEffect(() => {
    if (!mounted) return;
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) {
          setVisible(true);
          visibleAtRef.current = Date.now();

          posthog.capture(POSTHOG_CAPTURE_BANNER_IMPRESSION, {
            screen,
            platform: getPlatform(),
            delay_ms: delay,
          });
        }
      });
    });
    return () => { cancelled = true; };
  }, [mounted]);

  // Shimmer every 3s
  useEffect(() => {
    if (!visible) return;
    const t = setInterval(() => setShimmer(true), 3000);
    return () => clearInterval(t);
  }, [visible]);

  useEffect(() => {
    if (shimmer) {
      const t = setTimeout(() => setShimmer(false), 800);
      return () => clearTimeout(t);
    }
  }, [shimmer]);

  // Pulse every 4s on icon + CTA
  useEffect(() => {
    if (!visible) return;
    const t = setInterval(() => setPulse(true), 4000);
    return () => clearInterval(t);
  }, [visible]);

  useEffect(() => {
    if (pulse) {
      const t = setTimeout(() => setPulse(false), 600);
      return () => clearTimeout(t);
    }
  }, [pulse]);

  // Dismiss: slide-out then unmount
  const handleDismiss = useCallback(() => {
    const dismissCount = Number(localStorage.getItem(DISMISS_COUNT_KEY) || 0) + 1;
    const timeVisibleMs = visibleAtRef.current ? Date.now() - visibleAtRef.current : 0;

    posthog.capture(POSTHOG_CAPTURE_BANNER_DISMISS, {
      screen,
      platform: getPlatform(),
      time_visible_ms: timeVisibleMs,
      dismiss_count: dismissCount,
    });

    localStorage.setItem(DISMISS_COUNT_KEY, String(dismissCount));
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setTimeout(() => setMounted(false), 500);
  }, [screen]);

  const handleInstall = useCallback(() => {
    const { isIOS, isAndroid } = getDevicePlatform();
    const platform = getPlatform();

    let storeUrl;
    if (isIOS) {
      storeUrl = IOS_URL;
    } else if (isAndroid) {
      storeUrl = ANDROID_URL
    }

    posthog.capture(POSTHOG_CAPTURE_BANNER_CLICK, {
      screen,
      platform,
      store_url: storeUrl,
    });

    if (storeUrl) {
      window.open(storeUrl, '_blank');
    }
  }, [screen]);

  if (!mounted) return null;

  return (
    <div
      className="smart-banner"
      style={{
        transform: visible ? 'translateY(0)' : 'translateY(-110%)',
        opacity: visible ? 1 : 0,
      }}
    >
      {/* Shimmer overlay */}
      <div
        className="smart-banner__shimmer"
        style={{
          transform: shimmer ? 'translateX(100%)' : 'translateX(-100%)',
          transition: shimmer ? 'transform 0.8s ease' : 'none',
        }}
      />

      {/* App icon */}
      <img
        src={pikkoLogo}
        alt="PikkoPay"
        className="smart-banner__icon"
        style={{ transform: pulse ? 'scale(1.1)' : 'scale(1)' }}
      />

      {/* Text content */}
      <div className="smart-banner__content">
        <div className="smart-banner__title-row">
          <span className="smart-banner__app-name">PikkoPay</span>
          <div className="smart-banner__stars">
            {[1, 2, 3, 4, 5].map((i) => (
              <svg key={i} width="10" height="10" viewBox="0 0 24 24" fill="#FFD43B">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            ))}
            <span className="smart-banner__rating">5.0 · 10 avis</span>
          </div>
        </div>
        <div className="smart-banner__subtitle">Scanne, paye, sors. Gratuit.</div>
      </div>

      {/* Install button */}
      <button
        className="smart-banner__cta"
        onClick={handleInstall}
        style={{ transform: pulse ? 'scale(1.05)' : 'scale(1)' }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
        </svg>
        Installer
      </button>

      {/* Close button */}
      <button className="smart-banner__close" onClick={handleDismiss} aria-label="Fermer">
        ×
      </button>
    </div>
  );
};

export default SmartBanner;
